import { gql } from '@apollo/client';
import { subYears } from 'date-fns';
import { formatSin, formatPhoneNumber } from '../../utils/Functions';
import { useAppDispatch } from '../../app/hooks';
import { showErrorSnackbar } from '../../features/snackbar/snackbarSlice';
import { ISigningOfficer } from '../../types/CreateDealForm';

// Purchaser form helpers shared by Create Worksheet and editing a worksheet's purchasers

export const blankPurchaser = (projectId: string) => ({
  project: projectId,
  corp: false,
  firstName: '',
  lastName: '',
  identifications: [],
  dob: subYears(new Date(), 18),
  streetAddress: '',
  city: '',
  province: '',
  country: '',
  postalCode: '',
  idType: "Driver's Licence",
  idNumber: '',
  idJurisdiction: 'Ontario',
  idExpiry: new Date(),
  proofType: '',
  proofNumber: '',
  proofExpiry: null,
  sin: '',
  primaryPhone: '',
  email: '',
  occupation: '',
  employer: '',
  directors: '',
  signingOfficers: [],
  purchaserType: 'Investor',
  businessNumber: '',
});

// Fields accepted by createWorksheetSales' NewWorksheetPurchaserInput (and saved by purchaserUpdateById)
export const PURCHASER_INPUT_FIELDS = [
  'project', 'email', 'firstName', 'lastName', 'corp', 'sin', 'dob', 'unit', 'streetAddress', 'city', 'province', 'country',
  'postalCode', 'occupation', 'employer', 'purchaserType', 'primaryPhone', 'idType', 'idNumber', 'idExpiry', 'idJurisdiction',
  'directors', 'proofExpiry', 'proofNumber', 'proofType', 'businessNumber',
];
// Non-null String fields in that input, which older purchaser records may be missing
const PURCHASER_REQUIRED_STRINGS = ['email', 'lastName', 'streetAddress', 'city', 'province', 'country', 'purchaserType', 'primaryPhone', 'idType', 'idNumber', 'idJurisdiction'];

// The purchaser's details in the shape the server accepts (no identifications)
export const purchaserFields = (purchaser: any, projectId: string) => {
  const record: any = {};
  PURCHASER_INPUT_FIELDS.forEach((field) => {
    if (purchaser[field] !== undefined) record[field] = purchaser[field];
  });
  record.project = projectId;
  record.signingOfficers = (purchaser.signingOfficers || []).map(({ __typename, ...officer }: any) => officer);
  if (purchaser._id) {
    PURCHASER_REQUIRED_STRINGS.forEach((field) => {
      if (record[field] == null) record[field] = '';
    });
  }
  return record;
};

// The record to save with purchaserUpdateById
export const purchaserUpdateRecord = (purchaser: any, projectId: string) => {
  const record = purchaserFields(purchaser, projectId);
  // Never blank out a saved SIN (it's a hidden field, so a missing value here doesn't mean it was cleared)
  if (!record.sin) delete record.sin;
  return record;
};

// A purchaserById result as form state. Values the purchaser doesn't have stay blank, instead of the
// new-purchaser defaults that saving would then write to their record.
export const purchaserToForm = (purchaserById: any, projectId: string) => {
  const { __typename, ...purchaser } = purchaserById;
  return {
    ...blankPurchaser(projectId),
    idExpiry: null,
    idType: '',
    idJurisdiction: '',
    purchaserType: '',
    ...Object.fromEntries(Object.entries(purchaser).filter(([, value]) => value !== null)),
    project: projectId,
    signingOfficers: (purchaser.signingOfficers || []).map(({ __typename, ...officer }: any) => officer),
    identifications: (purchaser.identifications || []).map((identification: any) => ({
      _id: identification._id,
      name: identification.name,
      getUrl: identification.getUrl,
    })),
  };
};

// PurchaserForm's input handlers, for a purchasersReducer state
export const usePurchaserFormHandlers = (purchasersState: any[], purchasersDispatch: any, setErrors: (errors: any) => void) => {
  const storeDispatch = useAppDispatch();

  const handleTextInput = async (event: any, index: number) => {
    if (event && event.target.id === 'streetAddress') {
      purchasersDispatch({ type: 'UPDATE', payload: { index: index, field: event.target.id, value: event.target.value } });
    }

    if (event && event.target.name) {
      let value = event.target.value;
      if (event.target.name === 'primaryPhone') {
        value = formatPhoneNumber(event.target.value);
      }
      if (event.target.name === 'sin') {
        value = formatSin(event.target.value);
      }
      purchasersDispatch({ type: 'UPDATE', payload: { index: index, field: event.target.name, value: value } });
    }
  };

  const handleSigningOfficerInput = async (event: any, signingOfficerIndex: number, purchaserIndex: number) => {
    if (event && event.target.name) {
      let signingOfficer = purchasersState[purchaserIndex].signingOfficers.map((signingOfficer: ISigningOfficer, index: number) => {
        if (index === signingOfficerIndex) {
          return {
            ...signingOfficer,
            [event.target.name]: event.target.value,
          };
        } else return signingOfficer;
      });

      purchasersDispatch({ type: 'UPDATE', payload: { index: purchaserIndex, field: 'signingOfficers', value: signingOfficer } });
    }
  };

  const handleDeleteSigningOfficer = (signingOfficerIndex: number, purchaserIndex: number) => {
    if (purchasersState[purchaserIndex].signingOfficers.length === 1) {
      storeDispatch(showErrorSnackbar('Must have at least 1 signing officer'));
      return;
    }
    let deleteSigningOfficer = purchasersState[purchaserIndex].signingOfficers.filter(
      (signingOfficer: ISigningOfficer, index: number) => signingOfficerIndex !== index
    );

    purchasersDispatch({ type: 'UPDATE', payload: { index: purchaserIndex, field: 'signingOfficers', value: deleteSigningOfficer } });
  };

  const addSigningOfficer = async (numIndex: number) => {
    let signingOfficers = [
      ...purchasersState[numIndex].signingOfficers,
      {
        fullName: '',
        dob: subYears(new Date(), 18),
        streetAddress: '',
        primaryPhone: '',
        email: '',
        sin: '',
      },
    ];

    purchasersDispatch({ type: 'UPDATE', payload: { index: numIndex, field: 'signingOfficers', value: signingOfficers } });
  };

  const handleDob = (newValue: any, signingOfficerIndex: number, purchaserIndex: number) => {
    if (newValue == 'Invalid Date') {
      setErrors('Invalid Date');
      return;
    } else {
      setErrors(null);
    }

    let signingOfficer = purchasersState[purchaserIndex].signingOfficers.map((signingOfficer: ISigningOfficer, index: number) => {
      if (index === signingOfficerIndex) {
        return {
          ...signingOfficer,
          dob: newValue,
        };
      } else return signingOfficer;
    });

    purchasersDispatch({ type: 'UPDATE', payload: { index: purchaserIndex, field: 'signingOfficers', value: signingOfficer } });
  };

  const handleDetails = async (
    city: string,
    province: string,
    postalCode: string,
    country: string,
    streetAddress: string,
    index: number
  ) => {
    purchasersDispatch({
      type: 'UPDATEMULTIPLE',
      payload: {
        index: index,
        values: [
          {
            city: city,
            province: province,
            postalCode: postalCode,
            country: country,
            streetAddress: streetAddress,
          },
        ],
      },
    });
  };

  const handleStreetAddress = async (value: string, index: number) => {
    purchasersDispatch({ type: 'UPDATE', payload: { index: index, field: 'streetAddress', value: value } });
  };

  const handleEndvestorInput = (event: any, numIndex: number) => {
    purchasersDispatch({ type: 'UPDATE', payload: { index: numIndex, field: 'purchaserType', value: event.target.innerText } });
  };

  const handleIdTypeInput = (event: any, idType: string, numIndex: number) => {
    purchasersDispatch({ type: 'UPDATE', payload: { index: numIndex, field: idType, value: event.target.innerText } });
  };

  const handleIdIssuedByInput = (event: any, numIndex: number) => {
    purchasersDispatch({ type: 'UPDATE', payload: { index: numIndex, field: 'idJurisdiction', value: event.target.innerText } });
  };

  const handleDateChange = (newValue: any, id: string, numIndex: number) => {
    if (newValue == 'Invalid Date') {
      setErrors('Invalid Date');
      return;
    } else {
      setErrors(null);
    }
    purchasersDispatch({ type: 'UPDATE', payload: { index: numIndex, field: id, value: newValue } });
  };

  const handleCorpChange = (e: any, numIndex: number) => {
    purchasersDispatch({
      type: 'UPDATEMULTIPLE',
      payload: {
        index: numIndex,
        values: [
          {
            corp: !purchasersState[numIndex].corp,
            signingOfficers: purchasersState[numIndex].corp
              ? []
              : [
                  {
                    fullName: '',
                    dob: subYears(new Date(), 18),
                    streetAddress: '',
                    email: '',
                    primaryPhone: '',
                    sin: '',
                  },
                ],
          },
        ],
      },
    });
  };

  return {
    handleTextInput,
    handleSigningOfficerInput,
    handleDeleteSigningOfficer,
    addSigningOfficer,
    handleDob,
    handleDetails,
    handleStreetAddress,
    handleEndvestorInput,
    handleIdTypeInput,
    handleIdIssuedByInput,
    handleDateChange,
    handleCorpChange,
  };
};

// `project` must be requested: the IDs' getUrl is built from the purchaser's project, which is only loaded when asked for
export const GETPURCHASER = gql`
  query purchaserById($_id: MongoID!) {
    purchaserById(_id: $_id) {
      _id
      project {
        _id
      }
      email
      firstName
      lastName
      corp
      sin
      dob
      identifications {
        _id
        name
        getUrl
      }
      streetAddress
      city
      province
      country
      postalCode
      occupation
      employer
      directors
      businessNumber
      signingOfficers {
        fullName
        dob
        sin
        primaryPhone
        streetAddress
        email
      }
      purchaserType
      primaryPhone
      idType
      idNumber
      idExpiry
      unit
      proofType
      proofNumber
      proofExpiry
      idJurisdiction
    }
  }
`;

export const UPDATEPURCHASER = gql`
  mutation purchaserUpdateById($_id: MongoID!, $record: UpdateByIdPurchaserInput!) {
    purchaserUpdateById(_id: $_id, record: $record) {
      record {
        _id
      }
    }
  }
`;

export const UPLOADID = gql`
  mutation uploadID($id: MongoID!, $name: String!, $file: Upload!) {
    uploadID(id: $id, name: $name, file: $file) {
      _id
    }
  }
`;

export const DELETEID = gql`
  mutation deletePurchaserId($_id: MongoID!, $documentId: MongoID!, $deleteFile: Boolean!, $type: String!) {
    deletePurchaserId(_id: $_id, documentId: $documentId, deleteFile: $deleteFile, type: $type) {
      _id
    }
  }
`;
